/**
 * FindMine Cart Integration - "You May Also Like" Widget
 *
 * Displays product recommendations in the cart drawer based on items currently in the cart.
 * Only loads and runs when the cart drawer is open to optimize performance and reduce
 * unnecessary API calls.
 *
 * Features:
 * - Lazy loads only when cart drawer opens
 * - Updates recommendations when cart items change
 * - Falls back to last successful match if no recommendations found
 * - Hides cart drawer slider when FindMine content is available
 *
 * @requires window.theme.apps.findmine.cart_application_id - FindMine cart application ID
 * @requires cart-drawer - Cart drawer custom element
 * @listens cart-state:changed - Fires when cart contents change
 * @listens cart-drawer:opened - Fires when cart drawer opens
 */

import { subscribe, EVENTS } from '@utilities/events';

if (!window.theme?.apps?.findmine?.cart_application_id) {
  console.warn('FindMine integration: FindMineCartApplicationID is not set.');
}

/**
 * Hides the cart drawer slider when FindMine content is displayed
 *
 * This prevents duplicate content showing when FindMine recommendations are available.
 */
const hideCartDrawerSlider = () => {
  const cartDrawerSlider = document.querySelector('.cart-drawer__slider-container');
  if(cartDrawerSlider) cartDrawerSlider.classList.add('hidden');
}

/**
 * Triggers FindMine product match for cart recommendations
 *
 * Attempts to get product recommendations from FindMine based on the provided product.
 * FindMine handles its own caching and fallback logic internally.
 *
 * @param {Object} params - Match parameters
 * @param {string} params.product_id - Product ID
 * @param {string} params.product_color_id - Color/variant identifier
 * @param {boolean} params.product_in_stock - Whether the product is in stock
 *
 * @global
 */
window.findMineCartMatch = ({ product_id, product_color_id, product_in_stock }) => {
  const matchData = {
    application: window.theme?.apps?.findmine?.cart_application_id,
    product_id,
    product_color_id,
    product_in_stock,
    return_pdp_item: true,
    event_handlers: {
      onFinish: () => {
        // Hide the cart drawer slider now that FindMine content is displayed
        hideCartDrawerSlider();
      }
    }
  };

  console.log(`FindMineCart.match attempting:`, matchData);
  window.FindMineCart?.match(matchData);
};

/**
 * Loads the FindMine external script
 *
 * Creates and injects the FindMine cart script tag into the page.
 * Returns a promise that resolves when the script is fully loaded and ready.
 *
 * @returns {Promise<void>} Resolves when FindMine script is loaded
 */
const loadFindMineScript = () => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `//js.findmine.com/${window.theme?.apps?.findmine?.cart_application_id}.module.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load FindMine script'));
    document.head.appendChild(script);
  });
};

/**
 * Handle cart state changes
 *
 * Updates FindMine recommendations when items are added, removed, or updated in the cart.
 * Only runs when the cart drawer is open to avoid unnecessary API calls.
 *
 * @listens cart-state:changed
 */
subscribe(EVENTS.CART_STATE_CHANGED, (data) => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer?.isOpen) return;

  const { state, changes } = data;

  const itemsChanged = changes && (changes.itemsAdded || changes.itemsRemoved || changes.itemsUpdated);
  if (!itemsChanged) return;

  if (!state.items || state.items.length === 0) {
    const findmineContainer = document.querySelector('#findmine-cart-container');
    if (findmineContainer) {
      findmineContainer.innerHTML = '';
    }
    return;
  }

  const firstItem = state.items[0];
  window.findMineCartMatch({
    product_id: firstItem.productId,
    product_color_id: firstItem.colorId,
    product_in_stock: firstItem.available || false,
  });
});

/**
 * Handle cart drawer opened event
 *
 * Triggers FindMine match when the cart drawer opens, ensuring recommendations
 * are displayed with the current cart contents. Uses the cart state provided
 * in the event detail for immediate access without additional queries.
 *
 * Loads the FindMine script on first drawer open and waits for it to initialize
 * before triggering the first match.
 *
 * @listens cart-drawer:opened
 */
subscribe(EVENTS.CART_DRAWER_OPENED, async (data) => {
  const state = data?.state;

  if (!state?.items || state.items.length === 0) {
    const findmineContainer = document.querySelector('#findmine-cart-container');
    if (findmineContainer) {
      findmineContainer.innerHTML = '';
    }
    return;
  }

  if (!window.FindMineCart) {
    try {
      await loadFindMineScript();
    } catch (error) {
      console.error('Failed to load FindMine script:', error);
      return;
    }
  }

  const firstItem = state.items[0];
  window.findMineCartMatch({
    product_id: firstItem.productId,
    product_color_id: firstItem.colorId,
    product_in_stock: firstItem.available || false,
  });
});
